
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { LoadingScreen } from "../../components/ui/loading-screen";

const CheckoutPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [planDetails, setPlanDetails] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });
  
  useEffect(() => {
    if (!location.state) {
      navigate("/pricing");
      toast.error("Please select a plan first");
      return;
    }
    
    setPlanDetails(location.state);
    
    // Detect country for payment method
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        setBillingInfo((prev) => ({
          ...prev,
          country: data.country_name || "",
        }));
        
        // Set payment method based on country
        if (data.country_code === "IN") {
          setPaymentMethod("razorpay");
        } else {
          setPaymentMethod("stripe");
        }
      })
      .catch((error) => {
        console.error("Error detecting country:", error);
      });
  }, [location.state, navigate, user]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo((prev) => ({ ...prev, [name]: value }));
  };
  
  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
  };
  
  const handleCheckout = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Select appropriate payment gateway
      if (paymentMethod === "stripe") {
        const response = await axios.post("/api/payments/create-stripe-checkout", {
          plan: planDetails.plan,
          interval: planDetails.interval,
          price: planDetails.price,
          billingInfo
        });
        
        window.location.href = response.data.url;
      } else if (paymentMethod === "razorpay") {
        const response = await axios.post("/api/payments/create-razorpay-order", {
          plan: planDetails.plan,
          interval: planDetails.interval,
          price: planDetails.price * 83, // Convert to INR (approximate)
          billingInfo
        });
        
        const options = {
          key: response.data.keyId,
          amount: response.data.amount,
          currency: "INR",
          name: "AssessPro",
          description: `${planDetails.plan} Plan (${planDetails.interval})`,
          order_id: response.data.orderId,
          handler: function(response) {
            handlePaymentSuccess(response, "razorpay");
          },
          prefill: {
            name: billingInfo.name,
            email: billingInfo.email,
          },
          notes: {
            plan: planDetails.plan,
            interval: planDetails.interval,
          },
          theme: {
            color: "#6366F1",
          },
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else if (paymentMethod === "paypal") {
        const response = await axios.post("/api/payments/create-paypal-order", {
          plan: planDetails.plan,
          interval: planDetails.interval,
          price: planDetails.price,
          billingInfo
        });
        
        window.location.href = response.data.approvalUrl;
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error.response?.data?.message || "Payment processing failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePaymentSuccess = async (paymentData, provider) => {
    try {
      await axios.post("/api/payments/verify", {
        provider,
        paymentData,
        plan: planDetails.plan,
        interval: planDetails.interval,
      });
      
      navigate("/payment-success");
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error("Payment verification failed. Please contact support.");
    }
  };
  
  const validateForm = () => {
    const requiredFields = ["name", "email", "address", "city", "country"];
    
    for (const field of requiredFields) {
      if (!billingInfo[field]) {
        toast.error(`Please fill out your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    
    return true;
  };
  
  if (!planDetails) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="container py-10">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Checkout</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Billing Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={billingInfo.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={billingInfo.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={billingInfo.address}
                      onChange={handleInputChange}
                      placeholder="123 Main St"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={billingInfo.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      id="state"
                      name="state"
                      value={billingInfo.state}
                      onChange={handleInputChange}
                      placeholder="NY"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">ZIP/Postal Code</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      value={billingInfo.zipCode}
                      onChange={handleInputChange}
                      placeholder="10001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      value={billingInfo.country}
                      onChange={handleInputChange}
                      placeholder="United States"
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={handlePaymentMethodChange}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-2 border rounded-md p-3">
                    <RadioGroupItem value="stripe" id="stripe" />
                    <Label className="flex-1 cursor-pointer" htmlFor="stripe">
                      <div className="flex items-center justify-between">
                        <span>Credit/Debit Card</span>
                        <div className="flex space-x-1">
                          <img
                            src="/visa.svg"
                            alt="Visa"
                            className="h-8 w-auto"
                          />
                          <img
                            src="/mastercard.svg"
                            alt="Mastercard"
                            className="h-8 w-auto"
                          />
                          <img
                            src="/amex.svg"
                            alt="American Express"
                            className="h-8 w-auto"
                          />
                        </div>
                      </div>
                    </Label>
                  </div>
                  
                  {billingInfo.country === "India" && (
                    <div className="flex items-center space-x-2 border rounded-md p-3">
                      <RadioGroupItem value="razorpay" id="razorpay" />
                      <Label className="flex-1 cursor-pointer" htmlFor="razorpay">
                        <div className="flex items-center justify-between">
                          <span>Razorpay (UPI, Netbanking, etc.)</span>
                          <img
                            src="/razorpay.svg"
                            alt="Razorpay"
                            className="h-8 w-auto"
                          />
                        </div>
                      </Label>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 border rounded-md p-3">
                    <RadioGroupItem value="paypal" id="paypal" />
                    <Label className="flex-1 cursor-pointer" htmlFor="paypal">
                      <div className="flex items-center justify-between">
                        <span>PayPal</span>
                        <img
                          src="/paypal.svg"
                          alt="PayPal"
                          className="h-8 w-auto"
                        />
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
          
          <div className="col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="font-medium">Plan:</span>
                  <span className="capitalize">{planDetails.plan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Interval:</span>
                  <span className="capitalize">{planDetails.interval}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Candidate Limit:</span>
                  <span>{planDetails.candidateLimit}</span>
                </div>
                <div className="border-t pt-4 flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>${planDetails.price}</span>
                </div>
                
                <Button
                  onClick={handleCheckout}
                  disabled={isLoading}
                  className="w-full mt-4"
                >
                  {isLoading ? "Processing..." : "Complete Purchase"}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center mt-2">
                  By completing your purchase, you agree to our Terms of Service and Privacy Policy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
